import ReactSelect, {
  type ClassNamesConfig,
  type GroupBase,
  type Props as ReactSelectProps,
} from "react-select";
import CreatableReactSelect, { type CreatableProps } from "react-select/creatable";
import { zIndex } from "../../config/theme";
import { cn } from "./cn";
import {
  CONTROL_BASE_CLASSES,
  CONTROL_FOCUS_CLASSES,
  CONTROL_MIN_HEIGHT_CLASSES,
  CONTROL_REST_CLASSES,
  type ControlSize,
} from "./Field";

export interface ComboboxExtras {
  hasError?: boolean;
  size?: ControlSize;
}

type StyledAway = "unstyled" | "styles" | "classNames" | "classNamePrefix";

export type ComboboxProps<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = Omit<ReactSelectProps<Option, IsMulti, Group>, StyledAway> & ComboboxExtras;

export type CreatableComboboxProps<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = Omit<CreatableProps<Option, IsMulti, Group>, StyledAway> & ComboboxExtras;

const comboboxStyles = {
  menuPortal: (base: Record<string, unknown>) => ({
    ...base,
    zIndex: zIndex.selectMenu,
    fontSize: "0.75rem",
  }),
};

function comboboxClassNames<Option, IsMulti extends boolean, Group extends GroupBase<Option>>(
  hasError: boolean,
  size: ControlSize,
): ClassNamesConfig<Option, IsMulti, Group> {
  const state = hasError ? "invalid" : "normal";

  return {
    control: ({ isFocused, isDisabled }) =>
      cn(
        CONTROL_BASE_CLASSES,
        "flex items-center gap-1",
        CONTROL_MIN_HEIGHT_CLASSES[size],
        isFocused ? CONTROL_FOCUS_CLASSES[state] : CONTROL_REST_CLASSES[state],
        isDisabled && "opacity-50 cursor-not-allowed",
      ),
    valueContainer: () => "flex flex-wrap items-center gap-1 py-1 max-h-24 overflow-y-auto",
    input: () => "m-0 p-0 text-content",
    singleValue: () => "text-content",
    placeholder: () => "text-content-faint",
    multiValue: () => "flex items-center rounded bg-subtle",
    multiValueLabel: () => "px-1.5 py-0.5 text-xs text-content",
    multiValueRemove: () =>
      "cursor-pointer rounded-r px-1 text-content-muted hover:bg-line-soft hover:text-content",
    indicatorsContainer: () => "flex shrink-0 items-center",
    indicatorSeparator: () => "hidden",
    dropdownIndicator: () => "px-1 text-content-muted hover:text-content",
    clearIndicator: () => "cursor-pointer px-1 text-content-muted hover:text-content",
    loadingIndicator: () => "px-1 text-content-muted",
    menu: () =>
      "absolute z-[99999] mt-1 w-full overflow-hidden rounded-lg border border-line bg-card shadow-dropdown",
    menuPortal: () => "z-[99999]",
    menuList: () => "max-h-[min(400px,60vh)] overflow-y-auto p-2",
    group: () => "py-1",
    groupHeading: () => "px-3 py-1 text-xs font-semibold uppercase text-content-muted",
    option: ({ isFocused, isSelected, isDisabled }) =>
      cn(
        "cursor-pointer rounded-md px-3 py-2 text-xs text-content",
        (isFocused || isSelected) && "bg-subtle",
        isDisabled && "cursor-not-allowed opacity-50",
      ),
    noOptionsMessage: () => "px-3 py-2 text-xs text-content-muted",
    loadingMessage: () => "px-3 py-2 text-xs text-content-muted",
  };
}

export function Combobox<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({ hasError = false, size = "md", ...rest }: ComboboxProps<Option, IsMulti, Group>) {
  return (
    <ReactSelect<Option, IsMulti, Group>
      menuPortalTarget={typeof document === "undefined" ? undefined : document.body}
      menuPosition="fixed"
      {...rest}
      unstyled
      styles={comboboxStyles}
      classNames={comboboxClassNames<Option, IsMulti, Group>(hasError, size)}
    />
  );
}

export function CreatableCombobox<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({ hasError = false, size = "md", ...rest }: CreatableComboboxProps<Option, IsMulti, Group>) {
  return (
    <CreatableReactSelect<Option, IsMulti, Group>
      menuPortalTarget={typeof document === "undefined" ? undefined : document.body}
      menuPosition="fixed"
      {...rest}
      unstyled
      styles={comboboxStyles}
      classNames={comboboxClassNames<Option, IsMulti, Group>(hasError, size)}
    />
  );
}
